import { ADMIN_CONSOLE_ACTIONS } from '@steado/admin-contract/activity';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { ScopePills } from '@/components/table/ScopePills';
import { useAuth } from '@/lib/auth/auth-context';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { formatDateTime, humanise } from '@/lib/format';

import { ACTIVITY_PAGE_SIZE, activityQueryOptions } from './activity-query';
import { summarise } from './summarise';
import { ENTITY_OPTIONS, type ActivitySearch } from './activity-search';

/**
 * Action first, when last: an audit trail is read as "what happened, to what,
 * by whom", and the timestamp is what you check once you have found the row
 * rather than what you scan for.
 */
const COLUMNS: readonly TableColumn[] = [
  { key: 'action', label: 'Action', width: '1.5fr' },
  { key: 'target', label: 'Against', width: '1.4fr' },
  { key: 'actor', label: 'By', width: '1.1fr' },
  // The summary rides under the timestamp rather than in a column of its own:
  // it is a sentence about the row, and giving it 1.6fr squeezed the four
  // columns anyone actually scans.
  { key: 'when', label: 'Recorded', width: '1.2fr' },
  { key: 'ip', label: 'From', width: '0.9fr' },
];

/** Radix-free, but the same problem: "no scope" needs a value of its own. */
const ALL_SCOPES = '__all__';

const ACTIVITY_SCOPES = [
  { id: ALL_SCOPES, label: 'Everything', count: undefined },
  { id: 'console', label: 'Console actions', count: 'console' },
  { id: 'app', label: 'App actions', count: 'app' },
] as const satisfies readonly {
  readonly id: string;
  readonly label: string;
  readonly count: 'console' | 'app' | undefined;
}[];

export interface ActivityScreenProps {
  readonly search: ActivitySearch;
  readonly onSearchChange: (next: ActivitySearch) => void;
}

export function ActivityScreen({ search, onSearchChange }: ActivityScreenProps) {
  const { api } = useAuth();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const query = useQuery(activityQueryOptions(api, search));
  const counts = useQuery(scopeCountsQueryOptions(api));

  const setFilter = useCallback(
    (patch: Partial<ActivitySearch>) => {
      setCursorStack([]);
      onSearchChange({ ...search, ...patch, cursor: undefined });
    },
    [search, onSearchChange],
  );

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, search.cursor]);
    onSearchChange({ ...search, cursor: next });
  }, [query.data, search, onSearchChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onSearchChange({ ...search, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [search, onSearchChange]);

  const filterSpecs: readonly FilterSpec[] = useMemo(
    () => [
      {
        id: 'action',
        label: 'Action',
        value: search.action,
        // Only the console's own actions are offered: the product's names are
        // an open set, and a dropdown of them would go stale. Filtering by one
        // of those is still reachable by clicking the action in a row.
        options: ADMIN_CONSOLE_ACTIONS.map((value) => ({ value, label: value })),
        onChange: (action) => {
          setFilter({ action });
        },
      },
      {
        id: 'entity',
        label: 'Entity',
        value: search.entity,
        options: ENTITY_OPTIONS.map((value) => ({ value, label: value })),
        onChange: (entity) => {
          setFilter({ entity });
        },
      },
    ],
    [search, setFilter],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((entry) => ({
        id: entry.id,
        cells: [
          <BadgeCell
            key="action"
            label={entry.action}
            // Console actions are the operator's own; app actions are the
            // product's. Telling them apart at a glance is the point.
            tone={entry.isConsoleAction ? 'accent' : 'mute'}
          />,
          <div key="target" className="flex min-w-0 flex-col gap-px">
            <span className="truncate text-[13px] text-foreground">{humanise(entry.entity)}</span>
            <span className="truncate font-mono text-xs text-muted-foreground">{entry.entityId}</span>
          </div>,
          <TextCell
            key="actor"
            value={entry.actorName ?? (entry.actorUserId === null ? 'system' : 'Unnamed')}
            // A row with no actor is the API acting on its own — a worker, the
            // cron, a webhook — not a person whose name went missing.
            {...(entry.actorUserId === null ? ({ tone: 'muted' } as const) : {})}
          />,
          <div key="when" className="flex min-w-0 flex-col gap-px">
            <span className="text-[13px] text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
            <span className="truncate text-xs text-muted-foreground">{summarise(entry)}</span>
          </div>,
          <TextCell key="ip" value={entry.ip} mono tone="muted" />,
        ],
      })),
    [query.data],
  );

  // The scope is the pill row, not a filter: it says which trail you are
  // reading, and Reset must not silently switch you to the other one.
  const isFiltered =
    search.action !== undefined ||
    search.entity !== undefined ||
    search.entityId !== undefined ||
    search.actorUserId !== undefined;

  /**
   * Whether anything at all narrowed this list.
   *
   * Separate from `isFiltered` because the two answer different questions: the
   * empty state asks "did I narrow my way into this", where picking a source
   * counts, and Reset asks "what should I clear", where it must not.
   */
  const isNarrowed = isFiltered || search.scope !== undefined;

  const onReset = useCallback(() => {
    setCursorStack([]);
    onSearchChange({ ...(search.scope === undefined ? {} : { scope: search.scope }) });
  }, [search.scope, onSearchChange]);

  const pageOffset = cursorStack.length * ACTIVITY_PAGE_SIZE;

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load the audit trail"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1200px] flex-col gap-3">
      <ScopePills
        label="Audit source"
        scopes={ACTIVITY_SCOPES.map((scope) => ({
          id: scope.id,
          label: scope.label,
          ...(counts.data === undefined || scope.count === undefined
            ? {}
            : { count: counts.data.activity[scope.count] }),
        }))}
        value={search.scope ?? ALL_SCOPES}
        onChange={(next) => {
          setFilter({ scope: next === ALL_SCOPES ? undefined : next });
        }}
      />

      {search.entityId === undefined ? null : (
        <p className="flex items-center gap-3 text-[13px] text-muted-foreground">
          Showing everything recorded against <span className="font-mono">{search.entityId}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFilter({ entityId: undefined });
            }}
            className="text-muted-foreground"
          >
            clear
          </Button>
        </p>
      )}

      <TableToolbar
        searchValue={search.entityId ?? ''}
        searchPlaceholder="Filter by entity id…"
        onSearchChange={(value) => {
          setFilter({ entityId: value === '' ? undefined : value });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
      />

      <DataTable
        caption="Audit trail"
        title={
          search.scope === 'app'
            ? 'What the app did'
            : search.scope === 'console'
              ? 'What operators did'
              : 'Everything recorded'
        }
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={query.isPending}
        emptyTitle={isNarrowed ? 'No matching entries' : 'Nothing recorded yet'}
        emptyBody={
          isNarrowed
            ? 'No entry matches the current filters. Clear them to see the whole trail for this source.'
            : 'Entries appear here as soon as anything happens: a sign-in, an onboarding step, or an operator action.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" size="sm" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={query.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={query.isPending}
          />
        }
      />
    </div>
  );
}
